import { useEffect, useRef, useState } from 'react'

/**
 * Blendet ein Element ein, sobald es in den Viewport scrollt.
 *
 * Gibt [ref, sichtbar] zurueck. Der Beobachter trennt sich nach dem ersten
 * Treffer wieder - die Einblendung soll einmal passieren, nicht bei jedem
 * Hoch- und Runterscrollen erneut.
 *
 * Faellt bewusst auf "sofort sichtbar" zurueck, wenn es keinen
 * IntersectionObserver gibt (jsdom in den Tests, sehr alte Browser). Die Seite
 * muss auch ohne Animation vollstaendig lesbar sein - andernfalls waeren die
 * Inhalte dort dauerhaft auf opacity 0.
 */
export function useReveal() {
	const ref = useRef(null)
	const [shown, setShown] = useState(false)

	useEffect(() => {
		const node = ref.current
		if (!node) return

		if (typeof IntersectionObserver === 'undefined') {
			setShown(true)
			return
		}

		const observer = new IntersectionObserver(
			entries => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setShown(true)
						observer.disconnect()
					}
				}
			},
			// Erst ausloesen, wenn das Element ein Stueck weit im Bild ist,
			// sonst laeuft die Animation noch am unteren Rand ab und ist vorbei,
			// bevor man hinsieht.
			{ threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
		)

		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	return [ref, shown]
}

/**
 * Bild in AVIF mit WebP-Fallback.
 *
 * width/height sind die Kodiergroessen und dienen nur dem Seitenverhaeltnis:
 * Sie reservieren den Platz vor dem Laden, damit beim Nachladen nichts
 * springt. Die tatsaechliche Darstellungsgroesse kommt aus dem CSS.
 */
export function Photo({ sources, alt, className, loading = 'lazy', width, height }) {
	return (
		<picture>
			<source srcSet={sources.avif} type="image/avif" />
			<source srcSet={sources.webp} type="image/webp" />
			<img
				src={sources.webp}
				alt={alt}
				className={className}
				loading={loading}
				decoding="async"
				width={width}
				height={height}
			/>
		</picture>
	)
}
